import { Col, Row } from "antd";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useCallback, useEffect, useState } from "react";
import { GetInvoiceCaja, GetInvoiceCajaExcel } from "../../../Service/Service.billing";
import { DatePicker } from 'antd';
import DataTable from "../../../Components/UI/DataTable/DataTable";
import { Celda, CeldaDoble, tituloDeNombre } from "../../../Components/UI/DataTable/piezas";
import { ChipEstado, ChipCategoria } from "../../../Components/UI/Chip";
import BotonesExportar from "../../../Components/UI/BotonesExportar";
import { useToast } from "../../../Components/UI/Aviso";
import { usePageSubtitulo } from "../../../context/PageHeaderContext";

const { RangePicker } = DatePicker;

/* ============================================================================
   SISTEMA-DE-DISENO · Caja General, migrada al kit.

   La consulta, su filtro de fechas y el endpoint del Excel son los de antes.
   Cambia la lectura: 9 columnas -- tres de ellas números sueltos sin símbolo
   de moneda y dos de ellas etiquetas de colores ajenas al sistema -- pasan a
   5, agrupando lo que se lee junto.

   El TOTAL es lo que se busca en un reporte de caja, así que manda; subtotal e
   IVA, que son su descomposición, van debajo.
   ========================================================================== */

const money = (v) => `$${parseFloat(v || 0).toFixed(2)}`;
const fecha = (v) => (v ? format(new Date(v), "dd/MM/yyyy", { locale: es }) : "");

function CajaGeneral() {

    const [filters, setFilters] = useState();

    const [loading, setLoading] = useState(false);
    const [cajaList, setCajaList] = useState([]);
    const [error, setError] = useState(null);
    const [descargando, setDescargando] = useState(false);
    const { mostrar } = useToast();

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);

    const columns = [
        {
            // El documento con su tipo y su fecha: es un solo dato para quien
            // busca una factura en la caja del día.
            key: 'num_documento',
            titulo: 'Documento',
            ancho: 190,
            render: (r) => (
                <CeldaDoble
                    principal={r.num_documento}
                    secundario={[r.documento, fecha(r.fecha_emision)].filter(Boolean).join(" · ")}
                    monoPrincipal
                />
            ),
        },
        {
            key: 'paciente',
            titulo: 'Paciente',
            render: (r) => (
                <Celda valor={r.paciente_factura?.nombres_completos ? tituloDeNombre(r.paciente_factura.nombres_completos) : "Sin determinar"} />
            ),
        },
        {
            // El total manda; subtotal e IVA son su descomposición.
            key: 'total',
            titulo: 'Total',
            ancho: 190,
            alinear: 'derecha',
            render: (r) => (
                <CeldaDoble
                    principal={money(r.total)}
                    secundario={`Subt ${money(r.subtotal)} · IVA ${money(r.iva)}`}
                    monoPrincipal
                />
            ),
        },
        {
            key: 'id_contifico',
            titulo: 'Contífico',
            ancho: 160,
            render: (r) =>
                r.id_contifico ? (
                    <ChipEstado tono="hecho">Autorizado</ChipEstado>
                ) : (
                    // No es un estado del proceso, es un envío que no llegó a
                    // completarse: se marca como dato incompleto y el motivo que
                    // devolvió Contífico sigue estando al pasar el cursor.
                    <ChipCategoria incompleto title={r.mensaje_contifico}>No autorizado</ChipCategoria>
                ),
        },
        {
            key: 'estado',
            titulo: 'Estado',
            ancho: 130,
            render: (r) =>
                r.estado === 'A' ? (
                    <ChipEstado tono="hecho">Activo</ChipEstado>
                ) : (
                    <ChipEstado tono="anulado">Inactivo</ChipEstado>
                ),
        },
    ];

    const handleDateChange = (dates) => {
        const filtrosFinales = {
            ...filters,
            rangoFechas: dates,
            fecha_inicial: dates?.[0]?.format("YYYY-MM-DD"),
            //fecha_final: dates?.[1]?.format("YYYY-MM-DD")
            fecha_final: dates?.[1]?.endOf("day").format("YYYY-MM-DD HH:mm:ss")

        };

        setFilters(filtrosFinales);
    };

    const handleExcel = async() => {
        setDescargando(true);

        try {
            const response = await GetInvoiceCajaExcel(filters);
            if(response.status === 200) {
                const link = document.createElement('a');
                const blob = response.data;
                const objectURL = URL.createObjectURL(blob);
                link.href = objectURL;
                link.download = `REPORTE DE VENTAS.xlsx`;
                link.click();
            } else {
                mostrar({ tipo: "error", texto: response?.message || "No se pudo generar el Excel." });
            }

        } catch (error) {
            // Antes este `catch` estaba VACÍO: se pulsaba el botón y no pasaba
            // absolutamente nada, sin rastro ni en la consola.
            mostrar({ tipo: "error", texto: error?.message || "No se pudo generar el Excel." });
        } finally {
            setDescargando(false);
        }
    };

    const fetchInitialData = useCallback(async (page = 1, limit = 10, filters) => {
        setLoading(true);
        const result = await GetInvoiceCaja(page, limit, filters);

        if (result && result.success) {

            const fetchInvoice = result.data.map((item, index) => ({
                ...item,
                key: item.idfactura,
                index
            }));
            setCajaList(fetchInvoice);
            setError(null);

            setTotal(result.pagination.total);
            setCurrentPage(result.pagination.page);
            setPageSize(result.pagination.limit);
        } else {
            // Antes un fallo dejaba la tabla EXACTAMENTE como estaba: el
            // usuario cambiaba el rango, no pasaba nada y seguía leyendo los
            // datos del rango anterior creyendo que eran los nuevos.
            setCajaList([]);
            setTotal(0);
            setError(result?.message || "No se pudo obtener el reporte de caja.");
        }

        setLoading(false);
    }, []);

    // Un filtro nuevo vuelve a la primera página. Antes esta dependencia
    // incluía `currentPage` y `pageSize`, que la propia consulta escribe: cada
    // cambio de página disparaba DOS peticiones idénticas al backend.
    useEffect(() => {
        fetchInitialData(1, pageSize, filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    usePageSubtitulo(loading ? "" : `${total} factura${total === 1 ? "" : "s"}`);

    return (
        <div className="page-content">
            <div className="container-fluid">
                <Row className="d-flex align-items-center justify-content-between mb-3">
                    <Col>
                        <ol className="breadcrumb m-0">
                            <li className="breadcrumb-item">Reportes</li>
                            <li className="breadcrumb-item active">Caja General</li>
                        </ol>
                    </Col>
                    <Col className="text-end d-flex justify-content-end align-items-center gap-2 flex-wrap">
                        <RangePicker
                            value={filters?.rangoFechas}
                            onChange={handleDateChange}
                            getPopupContainer={() => document.body}
                        />

                        {/* El archivo lo arma el backend, donde `billing.export`
                            es barrera real. El rango sigue siendo obligatorio,
                            como antes: el botón se apaga, pero ahora dice por qué. */}
                        <BotonesExportar
                            permiso="billing.export"
                            titulo="Caja General"
                            idPrefijo="caja"
                            formatos={["excel"]}
                            bloqueo={filters?.rangoFechas ? null : "Elige primero un rango de fechas"}
                            cargando={descargando}
                            onExcel={handleExcel}
                        />

                    </Col>

                </Row>

                <DataTable
                    columnas={columns}
                    filas={cajaList}
                    filaKey="idfactura"
                    cargando={loading}
                    error={error}
                    onReintentar={() => fetchInitialData(currentPage, pageSize, filters)}
                    etiquetaTabla="Reporte de caja general"
                    hayFiltrosAplicados={Boolean(filters?.rangoFechas)}
                    onLimpiarFiltros={() => handleDateChange(null)}
                    textoVacioSinFiltros="No hay facturas emitidas todavía."
                    paginacion={{
                        page: currentPage,
                        pageSize,
                        total,
                        tamanos: [20, 50, 100],
                        onCambio: (p, ps) => {
                            setCurrentPage(p);
                            setPageSize(ps);
                            fetchInitialData(p, ps, filters);
                        },
                    }}
                />

            </div>

        </div>
    )
}
export default CajaGeneral;
